"use client";

import { useRef, useState, type KeyboardEvent } from "react";
import { PieceArt } from "@/components/board/PieceArt";
import { isKnightMove } from "@/lib/learn";
import { LearnIcon } from "./LearnIcon";

const squares = Array.from({ length: 64 }, (_, index) => `${"abcdefgh"[index % 8]}${8 - Math.floor(index / 8)}`);

export function KnightDiscovery() {
  const [found, setFound] = useState<string[]>([]);
  const [attempt, setAttempt] = useState<string | null>(null);
  const [hints, setHints] = useState(false);
  const [focus, setFocus] = useState(35);
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);
  const correct = attempt !== null && isKnightMove("d4", attempt);
  const message = found.length === 8 ? "All eight! A knight in the centre has plenty of possibilities."
    : attempt === "d4" ? "That’s your knight. Pick a square it can jump to."
    : correct ? `Yes, ${attempt}! Two squares one way, one to the side. Find another?`
    : attempt ? `Not quite ${attempt}. Try two squares up, then one to the side.`
    : "Pick a square the knight can reach from d4.";

  function moveFocus(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    const offsets: Record<string, number> = { ArrowRight: 1, ArrowLeft: -1, ArrowUp: -8, ArrowDown: 8 };
    if (!(event.key in offsets)) return;
    event.preventDefault();
    const row = Math.floor(index / 8), column = index % 8;
    const next = event.key === "ArrowLeft" || event.key === "ArrowRight"
      ? row * 8 + Math.max(0, Math.min(7, column + offsets[event.key]))
      : Math.max(0, Math.min(7, row + offsets[event.key] / 8)) * 8 + column;
    setFocus(next); buttons.current[next]?.focus();
  }

  return <section className="learn-discovery" id="try-a-move" aria-labelledby="discovery-title">
    <div className="learn-discovery-copy">
      <p className="learn-kicker"><LearnIcon name="spark" size={16} /> A little discovery</p>
      <h2 id="discovery-title">Take the<br /><em>unexpected route.</em></h2>
      <p>The knight does things differently. Two squares in one direction, then one to the side. A little leap. A whole new possibility.</p>
      <div className="learn-challenge-status" role="status"><span className={correct || found.length === 8 ? "is-correct" : ""}>{message}</span><small>{found.length} of 8 squares discovered</small></div>
      <div className="learn-challenge-actions">
        <button className="learn-text-link" onClick={() => setHints(!hints)} aria-pressed={hints}>{hints ? "Hide the hints" : "Show me a hint"}<LearnIcon name="spark" size={16} /></button>
        <button className="learn-icon-button" aria-label="Reset knight exercise" onClick={() => { setFound([]); setAttempt(null); setHints(false); }}><LearnIcon name="reset" size={17} /></button>
      </div>
    </div>
    <div className="learn-discovery-board-wrap">
      <div className="learn-mini-board" role="group" aria-label="Knight movement exercise" aria-describedby="knight-keyboard-help">
        {squares.map((square, index) => {
          const discovered = found.includes(square);
          return <button key={square} ref={(node) => { buttons.current[index] = node; }}
            className={`learn-square ${(Math.floor(index / 8) + index % 8) % 2 === 0 ? "is-light" : "is-dark"}${square === "d4" ? " is-knight" : ""}${discovered ? " is-found" : ""}${square === attempt && !correct && square !== "d4" ? " is-miss" : ""}`}
            tabIndex={focus === index ? 0 : -1} onFocus={() => setFocus(index)} onKeyDown={(event) => moveFocus(event, index)}
            onClick={() => { setAttempt(square); if (isKnightMove("d4", square)) setFound((items) => items.includes(square) ? items : [...items, square]); }}>
            <span className="sr-only">{`${square}${square === "d4" ? ", white knight" : discovered ? ", discovered" : ", empty square"}`}</span>
            {index % 8 === 0 && <span className="learn-rank" aria-hidden="true">{square[1]}</span>}
            {index >= 56 && <span className="learn-file" aria-hidden="true">{square[0]}</span>}
            {square === "d4" ? <PieceArt type="n" color="w" finish="marble" /> : discovered ? <LearnIcon name="check" size={21} /> : hints && isKnightMove("d4", square) ? <span className="learn-hint-dot" /> : null}
          </button>;
        })}
      </div>
      <p id="knight-keyboard-help">Click a square · or use arrow keys and Enter</p>
    </div>
  </section>;
}
